import React, { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'next/router';
import { App } from '@typedream/data';
import {
  DEFAULT_MAX_ITEMS,
} from '../constants';
import {
  ICollectionData,
  ICollectionDataItem,
  ICollectionDataItemMetadataKeys,
  ICollectionIntegration,
  ICollectionPageDataItem,
  IConnectCollectionRequest,
  ICustomizationUpdateRequest,
} from '../../../../../types';
import { CollectionSettingsPathContext } from '../../../context/CollectionSettingsContextProvider/pathContext';
import EditorTool from '../../../EditorTool';
import {
  BLOCK_ELEMENT_KEY, DUPLICATED_TABLE_ID_PREFIX, ICollectionTemplate, NOTION_KEYS,
} from '../../../types';
import { errToString } from '../../../../../utils/errors';
import { CollectionSettingsDataContext } from '../contextProvider';
import templateHomePage from '../templateHomePage';
import CollectionConnectTableNotion from '../../../../CollectionSettingsComponents/CollectionConnectTableNotion';
import { DEFAULT_ITEM_PAGE_TEMPLATES } from '../../../../CollectionSettingsComponents/itemPageTemplates';
import { COLLECTION_CONNECT_STEPS, COLLECTION_CONNECT_STEPS_STATUS } from '../../../../CollectionSettingsComponents/constants';
import CollectionConnectingTable from '../../../../CollectionSettingsComponents/CollectionConnectingTable';
import { useSpaceAndPageIDFromRouter } from '../../../../../data/space/hooks';
import {
  useDeletePageMutation,
  useGetPageQuery,
  useGetSpaceQuery,
  useListPagesQuery,
  usePagesPaths,
  useUpdatePageCustomizationMutation,
  useUpdateSpaceCustomizationPartialMutation,
} from '../../../../../store';
import { useTEditorRef } from '../../../plate';
import { api } from '../../../../../data/api';

function ConnectTableNotion() {
  const router = useRouter();
  const editor = useTEditorRef();
  const [tableID, settableID] = useState('');
  const [tableName, settableName] = useState('');
  const [loading, setloading] = useState(false);
  const [slug, setslug] = useState('');
  const [itemPageDataID, setitemPageDataID] = useState(null);
  const path = useContext(CollectionSettingsPathContext);
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const pagesPaths = usePagesPaths(spaceID);
  const { data: pages } = useListPagesQuery([spaceID]);
  const { data: space } = useGetSpaceQuery([spaceID]);
  const { data: page } = useGetPageQuery([spaceID, pageID]);
  const [finished, setfinished] = useState(false);
  const {
    collectionData,
    element,
    seterr,
    err,
    connectCollectionGenerateColumns,
    connectCollection,
    updatePageCollectionData,
    editorSyncData,
    tables,
    listTables,
    loadingListTables,
    getDefaultItemPageSchema,
    listColumns,
    loadingListColumns,
    totalItemsGeneratedColumns,
    columns,
  } = useContext(CollectionSettingsDataContext);

  const [loadingCollectionGenerateColumns, setloadingCollectionGenerateColumns] = useState(false);
  const [successGenerateColumns, setsuccessGenerateColumns] = useState(false);
  const [loadingCollectionConnectTable, setloadingCollectionConnectTable] = useState(false);
  const [successConnectTable, setsuccessConnectTable] = useState(false);
  const [loadingFinishConnecting, setloadingFinishConnecting] = useState(false);
  const [successFinishConnecting, setsuccessFinishConnecting] = useState(false);

  const currentIntegration = ICollectionIntegration.NOTION;

  const checkIsColumnExist = (col: string | string[]): boolean => {
    if (typeof col === 'string') {
      return Object.keys(columns).findIndex((item) => item === col) !== -1;
    }
    col.forEach((item) => {
      const isFound = Object.keys(columns).findIndex((column) => column === item) !== -1;
      if (!isFound) { return false; }
      return true;
    });

    return true;
  };

  const onCancel = () => {
    if (collectionData
      && collectionData?.[NOTION_KEYS.TABLE_ID]) {
      settableID(collectionData?.[NOTION_KEYS.TABLE_ID]);
    }
  };
  useEffect(() => {
    onCancel();
  }, [collectionData]);

  const connectingSteps = {
    [COLLECTION_CONNECT_STEPS.GENERATE_COLUMNS]: (successGenerateColumns
      && COLLECTION_CONNECT_STEPS_STATUS.COMPLETED) || (loadingCollectionGenerateColumns
        && COLLECTION_CONNECT_STEPS_STATUS.LOADING) || undefined,
    [COLLECTION_CONNECT_STEPS.CONNECTING_COLLECTION]: (successConnectTable
      && COLLECTION_CONNECT_STEPS_STATUS.COMPLETED) || (loadingCollectionConnectTable
        && COLLECTION_CONNECT_STEPS_STATUS.LOADING) || undefined,
    [COLLECTION_CONNECT_STEPS.FINISHED_CONNECTING]: (successFinishConnecting
      && COLLECTION_CONNECT_STEPS_STATUS.COMPLETED) || (loadingFinishConnecting
        && COLLECTION_CONNECT_STEPS_STATUS.LOADING) || undefined,
  };

  const connectTable = async () => {
    setsuccessFinishConnecting(false);
    setsuccessGenerateColumns(false);
    setsuccessConnectTable(false);
    setloadingFinishConnecting(false);
    seterr(null);
    if (!slug) {
      seterr('Please fill in a URL for the collection');
      return;
    }

    setloading(true);

    // ==== Check if collection is from a duplicated page ==== //
    const connDataID = element?.[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID];
    const pageCollDataItem = page?.customization?.collection_data?.[connDataID];
    if (
      pageCollDataItem
      && pageCollDataItem?.[NOTION_KEYS.TABLE_ID]
      && (pageCollDataItem[NOTION_KEYS.TABLE_ID]).startsWith(DUPLICATED_TABLE_ID_PREFIX)
    ) {
      setsuccessGenerateColumns(true);
      try {
        // If yes, we're just gonna replace the TEMPLATE_XXX with the selected page id
        // Replace table id in current page
        const newConnDataItem: ICollectionDataItem = {
          [NOTION_KEYS.TABLE_ID]: tableID,
          integration: currentIntegration,
          metadata: pageCollDataItem.metadata,
        };

        // Check if sort/search/filter has required column, if not, remove it
        if (newConnDataItem.metadata?.filter_column
          && !checkIsColumnExist(newConnDataItem.metadata?.filter_column)) {
          newConnDataItem.metadata.filter_column = undefined;
          newConnDataItem.metadata.filter_column_type = undefined;
          newConnDataItem.metadata.filter_condition = undefined;
          newConnDataItem.metadata.filter_value = undefined;
        }
        if (newConnDataItem.metadata?.sort_column
          && !checkIsColumnExist(newConnDataItem.metadata?.sort_column)) {
          newConnDataItem.metadata.sort_column = undefined;
          newConnDataItem.metadata.sort_direction = undefined;
        }
        if (newConnDataItem.metadata?.search_columns
          && !checkIsColumnExist(newConnDataItem.metadata?.search_columns)) {
          newConnDataItem.metadata.search_columns = [];
        }

        newConnDataItem.query = new App(api().api).app(pageCollDataItem)?.getDataQuery(
          spaceID,
          newConnDataItem,
          undefined,
        );

        // add new collection data
        await updatePageCollectionData({
          [connDataID]: newConnDataItem,
        });

        const spaceCollItemData = space?.customization?.collection_item_data
        && Object.values(space?.customization?.collection_item_data);
        const isCollItemDataExist = spaceCollItemData?.findIndex(
          (i) => i.tableID === tableID,
        ) !== -1;

        /* Space Customization */
        let customizationUpdateRequests: ICustomizationUpdateRequest[];
        const templateItemPageDataID = new App(api().api).app(
          pageCollDataItem,
        )?.getItemPageDataID(pageCollDataItem);
        const spaceCollData = space?.customization?.collection_item_data?.[templateItemPageDataID];

        if (isCollItemDataExist) {
          customizationUpdateRequests = [
            {
              customization_path: ['collection_item_data', templateItemPageDataID],
              customization_value: null,
              customization_delete: true,
            },
          ];
        } else if (spaceCollData) {
          // Replace table id in space customization
          const newItemPageDataID = new App(api().api).app(
            newConnDataItem,
          )?.getItemPageDataID(newConnDataItem);
          const newSpaceCollData = {
            ...spaceCollData,
            [NOTION_KEYS.TABLE_ID]: tableID,
          };

          customizationUpdateRequests = [
            {
              customization_path: ['collection_item_data', newItemPageDataID],
              customization_value: newSpaceCollData,
            },
            {
              customization_path: ['collection_item_data', templateItemPageDataID],
              customization_value: null,
              customization_delete: true,
            },
          ];
        }
        if ((isCollItemDataExist || spaceCollData) && customizationUpdateRequests) {
          await useUpdateSpaceCustomizationPartialMutation
            .run([{ spaceID, customizationUpdateRequests }]).unwrap();
        }

        /* Page Customization */
        // Replace table id in template page (only if there's an item page connected)
        if (spaceCollData && spaceCollData?.childrenTemplatePageID) {
          const { childrenTemplatePageID } = spaceCollData;
          const templateItemPageData = pages?.
            [childrenTemplatePageID]?.customization?.item_page_data;
          if (isCollItemDataExist) {
            /**
              * If using existing connected table:
              * delete child collection page,
              * and refetch the page
              */

            const childPagePath = pagesPaths?.[childrenTemplatePageID]?.path;

            let pageSlug = '';
            let pagePath = [];
            if (childPagePath && childPagePath.length > 0) {
              pageSlug = childPagePath[childPagePath.length - 1];
              if (childPagePath.length > 1) {
                pagePath = childPagePath.slice(0, -1);
              } else {
                pagePath = [];
              }
            }
            await useDeletePageMutation.run([{
              spaceID, pageID: childrenTemplatePageID, slug: pageSlug, pagePath,
            }]).unwrap();
          } else {
            // Replace table id in template page
            await useUpdatePageCustomizationMutation.run([{
              spaceID,
              pageID: childrenTemplatePageID,
              customizationUpdateRequests: [
                {
                  customization_path: ['item_page_data'],
                  customization_value: {
                    ...templateItemPageData,
                    [NOTION_KEYS.TABLE_ID]: tableID,
                  },
                },
              ],
            }]).unwrap();
          }
        }

        setsuccessConnectTable(true);
        setloadingFinishConnecting(true);
        await editorSyncData(newConnDataItem);
        setsuccessFinishConnecting(true);
      } catch (e) {
        seterr(errToString(e));
      }
      setloading(false);
      setfinished(true);
      setloadingFinishConnecting(false);
      return;
    }
    // ==== END: Check if collection is from a duplicated page ==== //

    // ==== Connect Collection ====
    // Collection data for the current page
    const collectionViewTemplate = element?.[BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE];
    const newConnDataID = element?.id;
    const newConnDataItem: ICollectionDataItem = {
      integration: currentIntegration,
      [NOTION_KEYS.TABLE_ID]: tableID,
      metadata: {
        [ICollectionDataItemMetadataKeys.MAX_ITEMS]: DEFAULT_MAX_ITEMS,
      },
    };
    newConnDataItem.query = new App(api().api).app(newConnDataItem)?.getDataQuery(
      spaceID,
      newConnDataItem,
      undefined,
    );

    // Template page customization
    const pageSchema = getDefaultItemPageSchema();
    const templatePageCustomization = {
      integration: collectionData?.integration,
      [NOTION_KEYS.TABLE_ID]: tableID,
      pageSchema,
      contentSchema: DEFAULT_ITEM_PAGE_TEMPLATES.template.schema,
      contentSchemaOrder: DEFAULT_ITEM_PAGE_TEMPLATES.template.schemaOrder,
      templateSelected: true,
    };
    const templatePageContent = DEFAULT_ITEM_PAGE_TEMPLATES.template.template;

    // Template home page customization
    const templateHomePageCollDataID = uuidv4();
    const templateHomePageCustomization: ICollectionDataItem = {
      integration: collectionData?.integration,
      [NOTION_KEYS.TABLE_ID]: tableID,
      metadata: { max_items: null },
    };
    templateHomePageCustomization.query = new App(api().api).app(
      templateHomePageCustomization,
    )?.getDataQuery(
      spaceID,
      templateHomePageCustomization,
      undefined,
    );
    const templateHomePageContent = templateHomePage(
      tableName,
      templateHomePageCollDataID,
      collectionViewTemplate,
    );

    // Space customization
    const spaceConnData: ICollectionPageDataItem = {
      integration: collectionData?.integration,
      [NOTION_KEYS.TABLE_ID]: tableID,
      childrenTemplatePageID: null, // templatePage?.id -> this will be added in the API
    };

    // payload
    const connectCollectionData: IConnectCollectionRequest = {
      page_id: pageID,
      update_page_customization: true,
      page_customization: {
        customization_path: ['collection_data', newConnDataID],
        customization_value: newConnDataItem,
      },
      template_page: {
        name: tableName,
        slug,
        path: [],
      },
      template_page_content: templatePageContent,
      template_home_page: {
        name: tableName,
        slug,
        path: [],
      },
      create_template_home_page: true,
      template_home_page_content: templateHomePageContent,
      template_home_page_customization: {
        customization_path: ['collection_data', templateHomePageCollDataID],
        customization_value: templateHomePageCustomization,
      },
      template_page_customization: {
        customization_path: ['item_page_data'],
        customization_value: templatePageCustomization,
      },
      space_customization: {
        customization_path: ['collection_item_data', itemPageDataID],
        customization_value: spaceConnData,
      },
      space_customization_template_id_key: 'childrenTemplatePageID',
    };

    try {
      EditorTool.setBlockProperty(
        editor,
        BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID,
        newConnDataID,
        { at: path },
      );
      if (space?.customization?.collection_item_data?.[itemPageDataID]?.childrenTemplatePageID) {
        // item page is already set up, just connect collection to this page
        setsuccessGenerateColumns(true);
        setsuccessConnectTable(true);
        setloadingFinishConnecting(true);
        const newConnData: ICollectionData = {
          [newConnDataID]: newConnDataItem,
        };
        await updatePageCollectionData(newConnData);
      } else {
        // generate columns and set up item pages
        setloadingCollectionGenerateColumns(true);
        setloadingCollectionConnectTable(true);
        const x = connectCollectionGenerateColumns(newConnDataItem, pageSchema);
        const y = connectCollection(connectCollectionData);
        await x;
        setloadingCollectionGenerateColumns(false);
        setsuccessGenerateColumns(true);
        await y;
        setloadingCollectionConnectTable(false);
        setsuccessConnectTable(true);
      }
      setloadingFinishConnecting(true);
      await editorSyncData(newConnDataItem);
      setsuccessFinishConnecting(true);
    } catch (e) {
      seterr(errToString(e));
    }
    setloading(false);
    setfinished(true);
    setloadingFinishConnecting(false);
  };

  useEffect(() => {
    const newCollData: ICollectionDataItem = {
      integration: currentIntegration,
      [NOTION_KEYS.TABLE_ID]: tableID,
    };
    setitemPageDataID(new App(api().api).app(newCollData)?.getItemPageDataID(newCollData));

    if (tableID !== '' && !tableID.startsWith(DUPLICATED_TABLE_ID_PREFIX)) {
      const connDataID = element?.[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID];
      const pageCollDataItem = page?.customization?.collection_data?.[connDataID];
      const newConnDataItem = {
        ...pageCollDataItem,
        [NOTION_KEYS.TABLE_ID]: tableID,
      };
      listColumns(newConnDataItem);
    }
  }, [tableID]);

  const childrenTemplatePageID = space?.customization?.collection_item_data?.[
    itemPageDataID]?.childrenTemplatePageID;

  const goToCollectionPage = () => {
    const url = `/col/${space?.slug}/${space?.id}/${childrenTemplatePageID}`;
    router.push(url);
  };

  const currentTemplate: ICollectionTemplate = element?.[BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE];
  const duplicateTableLink = currentTemplate?.databaseTemplateURL?.[currentIntegration];

  return (
    <>
      <CollectionConnectTableNotion
        listTables={listTables}
        tables={tables}
        tableID={tableID}
        tableName={tableName}
        settableName={settableName}
        slug={slug}
        setslug={setslug}
        collectionData={collectionData}
        settableID={settableID}
        loadingListTables={loadingListTables}
        itemPageDataID={itemPageDataID}
        goToCollectionPage={goToCollectionPage}
        connectTable={connectTable}
        loading={loading || loadingListColumns}
        err={err}
        seterr={seterr}
        allowConnectIfConnected
        onCancel={onCancel}
        finished={finished}
        setfinished={setfinished}
        duplicateTableLink={duplicateTableLink}
        showConnectTable
        showCancelConnectTable
        loadingListColumns={loadingListColumns}
      />
      {
        loading
        && (
          <CollectionConnectingTable
            integration={currentIntegration}
            completedSteps={connectingSteps}
            onGoBack={null}
            err={err}
            totalItemsGeneratedColumns={totalItemsGeneratedColumns}
          />
        )
      }
    </>
  );
}

export default ConnectTableNotion;
